import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import queryString from 'query-string';
import Table from 'react-bootstrap/Table';
import CounterpartiesTableFiltersBar from './components/CounterpartiesTableFiltersBar';
import CounterpartiesTableHead from './components/CounterpartiesTableHead';
import CounterpartiesTableRow from './components/CounterpartiesTableRow';
import i18nContext from 'components/i18n-context';
import { DIRECTION, MENU_DIRECTION } from 'components/constants';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Loader from 'components/Loader';
import Pagination from 'components/Pagination/Pagination';
import { Container } from 'uikit/Container/Container';
import './CounterpartiesTable.scss';

const CounterpartiesTable = ({ counterpartiesStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();

  const updateFiltersInUrl = (filters, pagination, sortColumn) => {
    const filtersParams = counterpartiesStore.prepareFiltersParams();
    const queryParams = {
      ...filtersParams,
      page: pagination.page,
      size: pagination.size,
      sort_column: sortColumn.sort_column,
      sort_direction: sortColumn.sort_direction
    };
    const searchParams = queryString.stringify(queryParams);
    navigate({ search: `?${searchParams}` }, { replace: true });
  };

  const loadFiltersFromUrl = () => {
    const params = queryString.parse(location.search);
    counterpartiesStore.setFiltersFromUrl(params);
  };

  useEffect(() => {
    loadFiltersFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!counterpartiesStore.isInitialized) {
      counterpartiesStore.getCounterpartiesList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartiesStore.isInitialized]);

  useEffect(() => {
    if (counterpartiesStore.isInitialized) {
      counterpartiesStore.resetCounterpartiesStore();
      loadFiltersFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    updateFiltersInUrl(counterpartiesStore.filters, counterpartiesStore.pagination, counterpartiesStore.sortColumn);
    if (counterpartiesStore.isInitialized) {
      counterpartiesStore.getCounterpartiesList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    counterpartiesStore.filters.search_text,
    counterpartiesStore.filters.action,
    counterpartiesStore.filters.show_internal,
    counterpartiesStore.pagination.size,
    counterpartiesStore.pagination.page,
    counterpartiesStore.sortColumn.sort_column,
    counterpartiesStore.sortColumn.sort_direction
  ]);

  const handleOnSortTable = (newSortBy) => {
    counterpartiesStore.setSortData({
      sortBy: newSortBy,
      direction:
        counterpartiesStore.sortColumn.sort_column !== newSortBy
          ? DIRECTION.ASC
          : counterpartiesStore.sortColumn.sort_direction === DIRECTION.ASC
            ? DIRECTION.DESC
            : DIRECTION.ASC
    });
  };

  if (!counterpartiesStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  return (
    <Container className={'counterparties-container'} header={i18n.getMessage('container.counterparties')}>
      <CounterpartiesTableFiltersBar
        isLoading={counterpartiesStore.isLoading}
        isInitialized={counterpartiesStore.isInitialized}
        filters={counterpartiesStore.filters}
        totalElements={counterpartiesStore.totalElements}
        handleSetFilters={counterpartiesStore.setFilter}
        handleExportCounterpartiesList={counterpartiesStore.exportCounterpartiesListCsv}
      />

      {counterpartiesStore.counterpartiesList?.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('counterparties.table.emptyPlug.header')}
          descriptionText={i18n.getMessage('counterparties.table.emptyPlug.description')}
        />
      ) : (
        <>
          <Pagination
            isShowPaginationSize={true}
            paginationSize={counterpartiesStore.pagination.size}
            handleChangePaginationSize={counterpartiesStore.setPageSize}
            isLoading={counterpartiesStore.isLoading}
            currentPage={counterpartiesStore.pagination.page}
            numberOfPages={counterpartiesStore.pagination.totalPages}
            setCurrentPage={counterpartiesStore.setPageNumber}
          />

          <Table>
            <thead>
              <CounterpartiesTableHead
                handleOnSortTable={handleOnSortTable}
                sortBy={counterpartiesStore.sortColumn.sort_column}
                direction={counterpartiesStore.sortColumn.sort_direction}
              />
            </thead>
            <tbody>
              {counterpartiesStore.counterpartiesList?.map((counterparty, index) => (
                <CounterpartiesTableRow
                  key={index}
                  type={'list'}
                  isLoading={counterpartiesStore.isLoading}
                  counterpartyData={counterparty}
                  handleChangeCounterPartyAction={counterpartiesStore.changeCounterpartyAction}
                />
              ))}
            </tbody>
          </Table>

          <Pagination
            isShowPaginationSize={true}
            paginationSize={counterpartiesStore.pagination.size}
            handleChangePaginationSize={counterpartiesStore.setPageSize}
            isLoading={counterpartiesStore.isLoading}
            currentPage={counterpartiesStore.pagination.page}
            numberOfPages={counterpartiesStore.pagination.totalPages}
            setCurrentPage={counterpartiesStore.setPageNumber}
            menuDirection={MENU_DIRECTION.UP}
          />
        </>
      )}
    </Container>
  );
};

CounterpartiesTable.propTypes = {
  counterpartiesStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  counterpartiesStore: stores.counterpartiesStore
}))(observer(CounterpartiesTable));
